/* @flow */

import { replaceWithObject } from '../helpers/string'
import { jsonToState } from '../helpers/json'
import { fetchApi, handleStatus, responseToJson, responseToBlob, fetchAuthHeader } from './index'

import type { IdType } from '../wizard/wizard'
import type { MetaType } from '../helpers/document'

type HandlerType = (mixed) => mixed

/**
 * @namespace api/atp
 */

/**
 * fetch lead
 * @param {string} id          id of lead
 * @param {function} handler   function to modify data after it is received
 * @return {CancelablePromise} lead as immutable object
 */
export const getLead = (url: string, mapping: Object) => (id: IdType, handler: HandlerType = (x) => x) => {
  const replace = replaceWithObject({ id: id })
  return fetchApi(replace(url), 'GET')
    .then(handleStatus)
    .then(responseToJson)
    .then(handler)
    .then(jsonToState(mapping))
}

/**
 * create lead
 * @param {object} data        data as json
 * @return {CancelablePromise} Map of lead
 */
export const createLead = (url: string, mapping: Object) => (data: Object) => {
  return fetchApi(url, 'POST', data)
    .then(handleStatus)
    .then(responseToJson)
    .then(fetchAuthHeader)
    .then(jsonToState(mapping))
}

/**
 * update lead
 * @param {string} id
 * @param {object} data        data as json
 * @return {CancelablePromise} Map of lead
 */
export const updateLead = (url: string, mapping: Object) => (id: IdType, data: Object) => {
  const replace = replaceWithObject({ id: id })
  return fetchApi(replace(url), 'PUT', data)
    .then(handleStatus)
    .then(responseToJson)
    .then(jsonToState(mapping))
}

/**
 * get url to go to DIS
 * @param  {string}  id
 * @return {CancelablePromise} Map of {quickAuthUrl, state}
 */
export const disLead = (url: string) => (id: IdType) => {
  const replace = replaceWithObject({ id: id })
  return fetchApi(replace(url), 'GET')
    .then(handleStatus)
    .then(responseToJson)
    .then(jsonToState({ quickAuthUrl: 'quick_auth_url' }))
}

/**
 * send sms to saved phoneNumber, phoneNumber is returned
 * @param {string} id   lead id
 * @return {CancelablePromise} {number}
 */
export const otpGenerate = (url: string) => (id: IdType) => {
  const replace = replaceWithObject({ id: id })
  return fetchApi(replace(url), 'POST')
    .then(handleStatus)
    .then(responseToJson)
}

/**
 * fetch auth in response
 * @param {string} id   lead id
 * @param {string} pin
 * @return {CancelablePromise}
 */
export const otpVerify = (url: string) => (id: IdType, pin: string) => {
  const replace = replaceWithObject({ id: id })
  return fetchApi(replace(url), 'POST', JSON.stringify({ pin: pin }))
    .then(handleStatus)
    .then(responseToJson)
    .then(fetchAuthHeader)
}

/**
 * fetches documents of lead
 * @param {string} id   lead id
 * @return {CancelablePromise} [{ id, step, field, filename, url, contentType }]
 */
export const listDocuments = (url: string) => (id: IdType) => {
  const replace = replaceWithObject({ id: id })
  return fetchApi(replace(url), 'GET')
    .then(handleStatus)
    .then(responseToJson)
    .then(jsonToState({}))
}

/**
 * upload document to server
 * @param {string}    id    lead id
 * @param {File|Blob} file
 * @param {object}    meta  { step, field }
 * @return {CancelablePromise} { id, step, field, filename, url, contentType }
 */
export const uploadDocument = (url: string) => (id: IdType) => (file: File, meta: MetaType = {}) => {
  const formData = new FormData()
  formData.append('file', file)
  for (const key in meta) {
    formData.append(key, meta[key])
  }
  const replace = replaceWithObject({ id: id })
  return fetchApi(replace(url), 'POST', formData)
    .then(handleStatus)
    .then(responseToJson)
    .then(jsonToState({}))
}

/**
 * delete a document on the server
 * @param {string} id     lead id
 * @param {string} docId  document id
 * @return {CancelablePromise}
 */
export const deleteDocument = (url: string) => (id: IdType, docId: string) => {
  const replace = replaceWithObject({ id: id, docId: docId })
  return fetchApi(replace(url), 'DELETE').then(handleStatus)
}

/**
 * download the document as blob
 * @param  {string} url absolute url to file
 * @return {CancelablePromise}  Blob
 */
export const downloadDocument = (url: string) => {
  return fetchApi(url)
    .then(handleStatus)
    .then(responseToBlob)
}
